import React from "react";
import { createPortal } from "react-dom";
import { Minus, Plus, X } from "lucide-react";

export default function FinanceBudgetModal({
  isOpen,
  onClose,
  theme,
  currentBudget,
  totalExpense,
  budgetInput,
  setBudgetInput,
  onSave,
}) {
  if (!isOpen) return null;

  const remainingBudget = currentBudget - totalExpense;
  const depletionRate = currentBudget > 0 ? (totalExpense / currentBudget) * 100 : 0;

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-end" style={{ zIndex: 9999 }}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm transition-opacity" onClick={onClose} />
      <div className="bg-white w-full rounded-t-[2.5rem] p-6 pb-12 relative z-10 animate-slide-up-mobile flex flex-col max-h-[90vh]">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-black text-gray-800 font-do-hyeon">연간 예산 관리</h3>
          <button onClick={onClose} className="p-2 bg-gray-100 rounded-full text-gray-500">
            <X size={20} />
          </button>
        </div>

        <div className="bg-gray-50 rounded-2xl p-5 mb-6 space-y-3 border border-gray-100">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-bold">💰 총 예산</span>
            <span className="font-black text-gray-800 text-base">{Number(currentBudget).toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 font-bold">💸 사용 금액 (연간)</span>
            <span className="font-black text-rose-500 text-base">-{totalExpense.toLocaleString()}</span>
          </div>
          <div className="h-px bg-gray-200 my-2"></div>
          <div className="flex justify-between text-base">
            <span className="text-gray-800 font-black">남은 예산</span>
            <span className={`font-black text-lg ${remainingBudget >= 0 ? "text-blue-600" : "text-rose-600"}`}>
              {remainingBudget.toLocaleString()}
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 mt-2 overflow-hidden">
            <div
              className={`h-2.5 rounded-full ${depletionRate > 100 ? "bg-rose-500" : "bg-blue-500"}`}
              style={{ width: `${Math.min(depletionRate, 100)}%` }}
            ></div>
          </div>
          <div className="text-xs text-right text-gray-400 font-bold">소진율 {depletionRate.toFixed(1)}%</div>
        </div>

        <form
          onSubmit={(e) => {
            onSave(e);
            onClose();
          }}
        >
          <label className="text-xs font-bold text-gray-500 mb-1.5 block ml-1">예산 수정</label>
          <div className="relative mb-4">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">₩</span>
            <input
              type="text"
              inputMode="numeric"
              value={budgetInput}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9]/g, "");
                setBudgetInput(val ? Number(val).toLocaleString() : "");
              }}
              className="w-full pl-10 pr-4 py-4 bg-[#F9F9F8] border-2 border-[#EAEAEA] rounded-2xl font-black text-xl outline-none focus:bg-white focus:border-gray-800 transition"
              placeholder="0"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex gap-1">
              <button
                type="button"
                onClick={() => {
                  const current = parseInt(budgetInput.replace(/,/g, "") || 0, 10);
                  const next = Math.max(0, current - 10000);
                  setBudgetInput(next.toLocaleString());
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-200 text-gray-500 hover:bg-gray-300 font-bold active:scale-95 transition"
              >
                <Minus size={14} />
              </button>
              <button
                type="button"
                onClick={() => {
                  const current = parseInt(budgetInput.replace(/,/g, "") || 0, 10);
                  const next = current + 10000;
                  setBudgetInput(next.toLocaleString());
                }}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#354F38]/10 text-[#354F38] hover:bg-[#354F38]/20 font-bold active:scale-95 transition"
              >
                <Plus size={14} />
              </button>
            </div>
          </div>
          <button
            type="submit"
            className={`w-full ${theme.bg} text-white py-4 rounded-2xl font-black text-lg shadow-lg active:scale-95 transition-transform`}
          >
            저장하기
          </button>
        </form>
      </div>
    </div>,
    document.body
  );
}
